/**
 * Sitze Privacy-Lock — reine Hilfen ohne PB-URL/Token (Unit-Tests).
 * ACL bleibt PocketBase listRule; dieser Client erfindet keine Sichtbarkeit.
 */

import type { KanzleiSeatForAktePicker } from './portalAktenTypes';

export type PortalDocumentShareAccess = 'read';

export interface PortalDocumentShare {
  id: string;
  document_id: string;
  customer_id: string;
  kanzlei_id: string;
  access: PortalDocumentShareAccess;
}

export type PortalDocumentVisibilityPart = 'owner' | 'akte' | 'shares';

export interface PortalDocumentVisibility {
  parts: PortalDocumentVisibilityPart[];
  chip: string;
  label: string;
}

/**
 * Privacy-Lock 01.09.2026: ACL = PB listRule (Owner / Akte-Kreis / Share).
 * Kein zweites kanzleiweites Client-Filter — Explorer listet, was PB liefert.
 */
export function buildPortalDocumentsPbFilter(_customerId: string, _kanzleiId?: string): string {
  return '';
}

export function portalSeatRootNcPath(kanzleiId: string, customerId: string): string {
  const kid = kanzleiId.trim();
  const cid = customerId.trim();
  if (!kid || !cid) return '';
  return `/Mandanten/${kid}/sitze/${cid}/`;
}

function portalPathAfterKnownRoot(norm: string): string | null {
  const prefixed = norm.startsWith('/') ? norm : `/${norm}`;
  const sitze = prefixed.match(/\/Mandanten\/[^/]+\/sitze\/[^/]+\//i);
  if (sitze && sitze.index != null) {
    return prefixed.slice(sitze.index + sitze[0].length);
  }
  const akten = prefixed.match(/\/Mandanten\/[^/]+\/akten\/[^/]+\//i);
  if (akten && akten.index != null) {
    return prefixed.slice(akten.index + akten[0].length);
  }
  const marker = '/portal-documents/';
  const idx = prefixed.toLowerCase().indexOf(marker);
  if (idx < 0) return null;
  return prefixed.slice(idx + marker.length);
}

/** Relativer Ordner unter Sitz-Root, Akte oder Legacy `portal-documents/` (leer = Wurzel). */
export function portalDocumentRelativeFolder(nextcloudPath?: string): string {
  const raw = String(nextcloudPath || '').trim();
  if (!raw) return '';
  const after = portalPathAfterKnownRoot(raw.replace(/\\/g, '/'));
  if (after == null) return '';
  const parts = after.split('/').filter(Boolean);
  if (!parts.length) return '';
  parts.pop();
  return parts.join('/');
}

/** Kundenfläche: Relativordner nur ohne Mandanten-/kz-/portal-documents-Leck. */
export function portalCustomerFacingRelativeFolder(nextcloudPath?: string): string {
  const rel = portalDocumentRelativeFolder(nextcloudPath);
  if (!rel) return '';
  if (/(?:^|\/)Mandanten(?:\/|$)|portal-documents|\bkz_[a-z0-9_]+|nextcloud|remote\.php\/dav/i.test(rel)) {
    return '';
  }
  return rel;
}

/** Relativer Ordner aus `portal_folders.nc_path` — trailing slash, kein Dateiname. */
export function portalFolderRelativeFromNcPath(ncPath?: string): string {
  const raw = String(ncPath || '').trim();
  if (!raw) return '';
  const after = portalPathAfterKnownRoot(raw.replace(/\\/g, '/'));
  if (after == null) return '';
  return after.split('/').filter(Boolean).join('/');
}

/** PB-Text-Id — nur nicht-leerer string, nie aus nextcloud_path. */
export function mapPbOptionalStringId(r: Record<string, unknown>, key: string): string | undefined {
  const v = r[key];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

export function mapPbRecordToDocumentShare(r: Record<string, unknown>): PortalDocumentShare | null {
  const id = mapPbOptionalStringId(r, 'id') || (typeof r.id === 'string' ? r.id : String(r.id ?? ''));
  const document_id = mapPbOptionalStringId(r, 'document_id') || '';
  const customer_id = mapPbOptionalStringId(r, 'customer_id') || '';
  const kanzlei_id = mapPbOptionalStringId(r, 'kanzlei_id') || '';
  if (!id || !document_id || !customer_id) return null;
  return {
    id,
    document_id,
    customer_id,
    kanzlei_id,
    access: 'read',
  };
}

export function describePortalDocumentVisibility(input: {
  viewerId: string;
  ownerId?: string;
  akteId?: string;
  shareCount: number;
}): PortalDocumentVisibility {
  const viewer = input.viewerId.trim();
  const owner = (input.ownerId || '').trim();
  const akte = (input.akteId || '').trim();
  const shares = input.shareCount > 0;
  const isOwner = Boolean(viewer && owner && viewer === owner);
  const parts: PortalDocumentVisibilityPart[] = [];
  if (isOwner) parts.push('owner');
  if (akte) parts.push('akte');
  if (shares) parts.push('shares');

  if (parts.length === 0) {
    return { parts, chip: 'Index', label: 'Sichtbar laut Dokumentenliste.' };
  }

  const chipBits: string[] = [];
  if (isOwner) chipBits.push('Owner');
  if (akte) chipBits.push('Akte');
  if (shares) chipBits.push(isOwner ? `Geteilt (${input.shareCount})` : 'Geteilt');

  const labelBits: string[] = [];
  if (isOwner) labelBits.push('Owner-Sitz (Du)');
  if (akte) labelBits.push('Akte-Kreis');
  if (shares) {
    labelBits.push(
      isOwner ? `explizite Shares (${input.shareCount})` : 'explizit mit Dir geteilt',
    );
  }

  return {
    parts,
    chip: chipBits.join(' · ') || 'Sichtbar',
    label: `Wer sieht das: ${labelBits.join(' · ')}.`,
  };
}

export function sharesForDocument(
  shares: PortalDocumentShare[],
  documentId: string,
): PortalDocumentShare[] {
  const id = documentId.trim();
  if (!id) return [];
  return shares.filter((s) => s.document_id === id);
}

export function shareTargetSeats(
  seats: KanzleiSeatForAktePicker[],
  args: { viewerId: string; alreadySharedIds: Set<string> },
): KanzleiSeatForAktePicker[] {
  const self = args.viewerId.trim();
  return seats.filter((s) => s.id && s.id !== self && !args.alreadySharedIds.has(s.id));
}

export function portalShareSeatLabel(seat: KanzleiSeatForAktePicker): string {
  const name = seat.name.trim();
  const email = seat.email.trim();
  if (name && email) return `${name} (${email})`;
  return name || email || seat.id;
}

/** Share-Popover am Icon — nicht als Fuß-Panel unter Ablage. */
export const SHARE_POPOVER_WIDTH_PX = 320;
export const SHARE_POPOVER_EST_HEIGHT_PX = 280;
export const SHARE_POPOVER_GAP_PX = 6;
export const SHARE_POPOVER_MARGIN_PX = 8;

export function placeSharePopover(
  anchor: { left: number; top: number; right: number; bottom: number },
  viewport: { width: number; height: number },
  panel?: { width?: number; height?: number },
): { left: number; top: number } {
  const width = panel?.width ?? SHARE_POPOVER_WIDTH_PX;
  const height = panel?.height ?? SHARE_POPOVER_EST_HEIGHT_PX;
  const gap = SHARE_POPOVER_GAP_PX;
  const margin = SHARE_POPOVER_MARGIN_PX;
  const maxLeft = Math.max(margin, viewport.width - width - margin);
  let left = anchor.right - width;
  left = Math.min(Math.max(left, margin), maxLeft);

  const spaceBelow = viewport.height - anchor.bottom - gap - margin;
  const spaceAbove = anchor.top - gap - margin;
  const preferBelow = spaceBelow >= Math.min(height, 140) || spaceBelow >= spaceAbove;
  let top = preferBelow ? anchor.bottom + gap : Math.max(margin, anchor.top - height - gap);
  const maxTop = Math.max(margin, viewport.height - height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top };
}
